import {Grid2, Typography} from "@mui/material";
import Divider from "@mui/material/Divider";
import {color_blue} from "@/agentur/AgenturColors";
import {BriefIcon, TelefonIcon} from "@/agentur/AgenturIcons";
import {Footer} from "@/app/footer/Footer";
import * as React from "react";
import {RefObject} from "react";
import {StatusFooter} from "@/app/footer/StatusFooter";
import {Section8DTO} from "@/pages/landing-page/landing-page-data";
import {compactValue} from "@/lib/screen-class";
import {LANDING_BODY_LARGE, LANDING_HEADLINE_SMALL, LANDING_LEAD} from "@/pages/landing-page/utils/landing-font-sizes";

/** Contact details break inside a long e-mail address or phone number below `md` instead of sticking out. */
const CONTACT_DETAIL_SX = { fontSize: LANDING_BODY_LARGE, overflowWrap: compactValue("anywhere", "normal") };

interface Section8Props {
    data: Section8DTO;
    sr: RefObject<any>;
}

export const Section8 = ({ sr, data }: Section8Props) => (
    <Grid2
        ref={sr}
        container
        direction="column"
        paddingTop={4}
        sx={{
            minHeight: "100%",
            position: "relative"
        }}
    >
        <Grid2
            width="100%"
            sx={{
                px: {
                    md: 1,
                    lg: 10
                }
            }}
        >
            <Typography sx={{ fontSize: LANDING_HEADLINE_SMALL }} fontWeight="700">
                {data.title}
            </Typography>
        </Grid2>
        <Grid2
            width="100%"
            sx={{
                px: {
                    md: 1,
                    lg: 10
                },
                paddingTop: {
                    md: 1,
                    lg: 5
                }
            }}
        >
            <Typography component="div" sx={{ fontSize: LANDING_LEAD }}>
                {data.text.split("\n").map((t) => (
                    <div key={t}>{t}</div>
                ))}
            </Typography>
        </Grid2>
        {/*<Grid2 width="100%" py={2}>*/}
        {/*    <Divider dir="horizontal" color={color_green[2]} sx={{ borderWidth: "2.5px" }} />*/}
        {/*</Grid2>*/}
        <Grid2 flexGrow={1} paddingBottom={6}>
            <Grid2
                container
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
                sx={{
                    paddingTop: {
                        xs: 5,
                        md: 10
                    },
                    gap: {
                        xs: 8,
                        md: 12
                    },
                    flexWrap: "wrap"
                }}
            >
                {data.contacts.map((contact, index) => (
                    <Grid2
                        key={contact.name}
                        container
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            width: compactValue("100%", "auto"),
                            gap: {
                                xs: 4,
                                md: 6
                            }
                        }}
                    >
                        {/* Below `md` the image takes the width up to its own 400 px and the SVG scales through
                            its `viewBox`; from `md` the SVG stays inline, its line box keeps the row at 446 px. */}
                        <Grid2
                            data-cy="contact-image"
                            sx={{
                                width: compactValue("min(400px, 100%)", "400px"),
                                "& > svg": { display: compactValue("block", "inline") }
                            }}
                        >
                            <svg
                                width="400"
                                height="440"
                                viewBox="0 0 400 440"
                                style={{ width: "100%", height: "auto" }}
                            >
                                <defs>
                                    <clipPath id={`polygon-clip-${index}`} clipPathUnits="objectBoundingBox">
                                        <polygon points="0.5,0 1,0.25 1,0.75 0.5,1 0,0.75 0,0.25" />
                                    </clipPath>
                                </defs>
                                <image
                                    href={contact.image}
                                    width="100%"
                                    height="100%"
                                    preserveAspectRatio="xMidYMid slice"
                                    clipPath={`url(#polygon-clip-${index})`}
                                />
                            </svg>
                        </Grid2>
                        <Grid2 container direction="column" marginTop={-1}>
                            <Grid2>
                                <Typography sx={CONTACT_DETAIL_SX} fontWeight="700">
                                    {contact.name}
                                </Typography>
                            </Grid2>
                            <Grid2>
                                <Typography sx={CONTACT_DETAIL_SX}>{contact.role}</Typography>
                            </Grid2>
                            <Grid2 container alignItems="center" gap={2} paddingTop={2}>
                                <Grid2>
                                    <TelefonIcon mode="light" />
                                </Grid2>
                                <Grid2>
                                    <Typography sx={CONTACT_DETAIL_SX}>
                                        <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                                    </Typography>
                                </Grid2>
                            </Grid2>
                            <Grid2 container alignItems="center" gap={2} paddingTop={2}>
                                <Grid2>
                                    <BriefIcon mode="light" />
                                </Grid2>
                                <Grid2>
                                    <Typography sx={CONTACT_DETAIL_SX}>
                                        <a href={`mailto:${contact.email}`}>{contact.email}</a>
                                    </Typography>
                                </Grid2>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                ))}
            </Grid2>
        </Grid2>
        <Grid2 sx={{ width: "100%" }}>
            <Divider dir="horizontal" color={color_blue[3]} sx={{ borderWidth: "1.5px" }} />
            <Footer landingPage />
            <StatusFooter />
        </Grid2>
    </Grid2>
);
